import React, { useEffect, Children } from 'react';
import { motion } from 'framer-motion';
import { Clock, CheckCircle2, XCircle, ArrowRight, FileText, AlertTriangle, Sparkles } from 'lucide-react';
const fadeUp = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.16, 1, 0.3, 1]
    }
  }
};
const staggerContainer = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15
    }
  }
};
export function LogisticsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <main className="relative z-10 bg-brand-black min-h-screen selection:bg-brand-orange/30" data-id="element-892">
      {/* 1. HERO SECTION */}
      <section className="relative pt-40 pb-32 md:pt-52 md:pb-40 overflow-hidden" data-id="element-893">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-orange/5 rounded-full blur-[120px] pointer-events-none" data-id="element-894" />

        <div className="max-w-5xl mx-auto px-6 relative z-10 text-center" data-id="element-895">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          ease: [0.16, 1, 0.3, 1]
        }} className="text-5xl md:text-7xl font-display mb-8 tracking-tight leading-[0.95] text-balance" data-id="element-896">
            Intelligent Logistics <br className="hidden md:block" data-id="element-897" />
            <span className="text-brand-orange" data-id="element-898">Without the Data Silos.</span>
          </motion.h1>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.1,
          ease: [0.16, 1, 0.3, 1]
        }} className="max-w-3xl mx-auto space-y-6 mb-12" data-id="element-899">
            <p className="text-lg md:text-xl text-gray-400 leading-relaxed font-light max-w-2xl mx-auto" data-id="element-900">
              Stop letting fragmented tracking data, manual customs
              documentation, and exception handling slow down your supply chain.
              We deploy autonomous AI agents that orchestrate data across
              systems and proactively manage exceptions.
            </p>
            <p className="text-lg md:text-xl text-white font-medium" data-id="element-901">
              If your team is manually tracking shipments, you're already
              behind.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.2,
          ease: [0.16, 1, 0.3, 1]
        }} className="flex flex-col sm:flex-row items-center justify-center gap-6" data-id="element-902">
            <a href="mailto:sales@aijentik.com" className="w-full sm:w-auto bg-brand-orange hover:bg-brand-deep-orange text-white px-8 py-4 rounded-full font-medium transition-all duration-300 shadow-[0_0_40px_rgba(219,127,74,0.3)] hover:shadow-[0_0_60px_rgba(219,127,74,0.4)] flex items-center justify-center gap-2" data-id="element-903">
              Get in Touch
              <ArrowRight className="w-4 h-4" data-id="element-904" />
            </a>
            <a href="#use-cases" className="w-full sm:w-auto text-white hover:text-brand-orange px-8 py-4 font-medium transition-colors flex items-center justify-center" data-id="element-905">
              See the Use Cases
            </a>
          </motion.div>
        </div>
      </section>

      {/* 2. INDUSTRY REALITY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-906">
        <div className="max-w-7xl mx-auto px-6" data-id="element-907">
          <div className="flex flex-col lg:flex-row gap-16 lg:gap-24 items-center" data-id="element-908">
            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={fadeUp} className="lg:w-1/2" data-id="element-909">
              <h2 className="text-4xl md:text-5xl font-display leading-[1.0] mb-6 text-balance tracking-tight" data-id="element-910">
                You're Not Just Moving Freight.
                <br data-id="element-911" />
                <span className="text-gray-500" data-id="element-912">You're Moving Data.</span>
              </h2>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: '-100px'
          }} variants={staggerContainer} className="lg:w-1/2" data-id="element-913">
              <motion.p variants={fadeUp} className="text-xl text-gray-400 leading-relaxed mb-10 font-light" data-id="element-914">
                Most logistics businesses aren't limited by physical capacity —
                they're capped by operational noise. Documents stuck in email
                threads, exceptions handled reactively, and clients left in the
                dark until someone manually checks a spreadsheet.
              </motion.p>

              <div className="space-y-8" data-id="element-915">
                {[{
                label: 'The Visibility Gap',
                desc: "Clients call for updates because your systems don't talk to each other — and your team spends hours compiling status reports manually."
              }, {
                label: 'The Exception Trap',
                desc: 'A delayed shipment triggers a cascade of manual calls, emails, and rescheduling — consuming your best people for hours.'
              }, {
                label: 'The Data Entry Bottleneck',
                desc: 'Bills of lading, customs forms, and delivery receipts are still being keyed in by hand — slowing every shipment and introducing errors.'
              }].map((point, i) => <motion.div key={i} variants={fadeUp} className="flex items-start gap-4" data-id="element-916">
                    <div className="mt-1 bg-red-500/10 p-1 rounded-full shrink-0" data-id="element-917">
                      <XCircle className="w-5 h-5 text-red-500/70" data-id="element-918" />
                    </div>
                    <div data-id="element-919">
                      <p className="text-lg text-white font-medium mb-1" data-id="element-920">
                        {point.label}
                      </p>
                      <p className="text-gray-400 font-light leading-relaxed" data-id="element-921">
                        {point.desc}
                      </p>
                    </div>
                  </motion.div>)}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. SOLUTION SECTION (AI AGENTS) */}
      <section id="use-cases" className="py-32 relative" data-id="element-922">
        <div className="max-w-7xl mx-auto px-6" data-id="element-923">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="max-w-3xl mb-20" data-id="element-924">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-925">
              Purpose-Built Agents for Supply Chains.
            </h2>
            <p className="text-xl text-gray-400 font-light leading-relaxed max-w-2xl" data-id="element-926">
              We don't sell AI tools. We build autonomous digital operators that
              understand logistics networks, access your TMS/WMS, and execute
              real coordination without human involvement.
            </p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-8" data-id="element-927">
            {[{
            icon: <FileText className="w-6 h-6 text-brand-orange" data-id="element-928" />,
            title: 'Document Automation',
            desc: 'Agents instantly extract data from BOLs, commercial invoices, and customs forms, entering it directly into your core systems.'
          }, {
            icon: <AlertTriangle className="w-6 h-6 text-brand-orange" data-id="element-929" />,
            title: 'Exception Management',
            desc: 'Automatically detects delays, reroutes, or missing documentation and proactively notifies relevant parties before it becomes a crisis.'
          }, {
            icon: <Clock className="w-6 h-6 text-brand-orange" data-id="element-930" />,
            title: 'Real-Time Visibility',
            desc: 'Aggregates tracking data from multiple carriers and provides customers with instant, accurate status updates via conversational interfaces.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-white/[0.02] border border-white/5 p-10 rounded-2xl hover:bg-white/[0.04] transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-brand-orange/30 hover:shadow-[0_20px_40px_-15px_rgba(219,127,74,0.15)] group" data-id="element-931">
                <div className="bg-brand-orange/10 w-12 h-12 rounded-xl flex items-center justify-center mb-8 group-hover:bg-brand-orange/20 transition-colors duration-500" data-id="element-932">
                  {card.icon}
                </div>
                <h3 className="text-xl font-medium text-white mb-4 tracking-tight" data-id="element-933">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-934">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 4. TRANSFORMATION SECTION (BEFORE vs AFTER) */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-935">
        <div className="max-w-7xl mx-auto px-6" data-id="element-936">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid lg:grid-cols-2 gap-12 lg:gap-0 relative" data-id="element-937">
            <div className="hidden lg:block absolute top-0 bottom-0 left-1/2 w-px bg-gradient-to-b from-transparent via-white/10 to-transparent -translate-x-1/2" data-id="element-938" />

            {/* Left: Traditional Model */}
            <motion.div variants={fadeUp} className="lg:pr-20" data-id="element-939">
              <h3 className="text-sm font-bold tracking-[0.25em] text-gray-500 uppercase mb-12" data-id="element-940">
                Traditional Model
              </h3>
              <div className="space-y-10" data-id="element-941">
                {['Operator spends 2 hours manually keying data from scanned documents into the TMS', 'Delayed container triggers 15 phone calls and a chain of manual email updates', 'Client calls at 4 PM asking for a status update — nobody has checked since morning'].map((text, i) => <div key={i} className="flex gap-6 opacity-60" data-id="element-942">
                    <XCircle className="w-6 h-6 text-gray-500 shrink-0" data-id="element-943" />
                    <p className="text-xl text-gray-400 font-light" data-id="element-944">{text}</p>
                  </div>)}
              </div>
            </motion.div>

            {/* Right: Aijentik Model */}
            <motion.div variants={fadeUp} className="lg:pl-20" data-id="element-945">
              <h3 className="text-sm font-bold tracking-[0.25em] text-brand-orange uppercase mb-12" data-id="element-946">
                Aijentik Model
              </h3>
              <div className="space-y-10" data-id="element-947">
                {['Documents scanned, extracted, validated, and filed in under 60 seconds — zero manual entry', 'Exception detected, stakeholders notified, and rerouting initiated automatically', 'Client received a proactive update 3 hours ago — before they even thought to ask'].map((text, i) => <div key={i} className="flex gap-6" data-id="element-948">
                    <Sparkles className="w-6 h-6 text-brand-orange shrink-0" data-id="element-949" />
                    <p className="text-xl text-white font-medium" data-id="element-950">{text}</p>
                  </div>)}
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 5. USE CASES SECTION */}
      <section className="py-32" data-id="element-951">
        <div className="max-w-7xl mx-auto px-6" data-id="element-952">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-4xl md:text-5xl font-display text-center mb-20 leading-[1.0] tracking-tight text-balance" data-id="element-953">
            What This Actually Looks Like <br className="hidden md:block" data-id="element-954" />
            <span className="text-gray-500" data-id="element-955">in Your Operations</span>
          </motion.h2>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-6" data-id="element-956">
            {[{
            title: 'Automated Quote Generation',
            desc: 'Instantly processes rate requests, checks capacity across carriers, and generates accurate quotes without manual calculation.'
          }, {
            title: 'Proactive Delay Routing',
            desc: 'Monitors weather, port congestion, and carrier updates to automatically suggest alternative routes when delays are detected.'
          }, {
            title: 'Invoice Reconciliation',
            desc: 'Automatically matches carrier invoices against original quotes and delivery receipts, flagging only discrepancies for human review.'
          }].map((card, i) => <motion.div key={i} variants={fadeUp} className="bg-[#0A0A0A] border border-white/10 p-10 rounded-2xl transition-all duration-700 ease-premium hover:-translate-y-2 hover:border-white/20 hover:shadow-2xl" data-id="element-957">
                <h3 className="text-2xl font-display mb-4 tracking-tight" data-id="element-958">
                  {card.title}
                </h3>
                <p className="text-gray-400 leading-relaxed font-light" data-id="element-959">
                  {card.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 6. METHODOLOGY SECTION */}
      <section className="py-32 bg-[#050505] border-y border-white/5" data-id="element-960">
        <div className="max-w-7xl mx-auto px-6" data-id="element-961">
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={fadeUp} className="text-center mb-20" data-id="element-962">
            <h2 className="text-4xl md:text-5xl font-display mb-6 leading-[1.0] tracking-tight text-balance" data-id="element-963">
              No Fluff. Just Execution.
            </h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          margin: '-100px'
        }} variants={staggerContainer} className="grid md:grid-cols-3 gap-12 relative" data-id="element-964">
            <div className="hidden md:block absolute top-8 left-[15%] right-[15%] h-px bg-white/10" data-id="element-965" />

            {[{
            step: '01',
            title: 'Audit',
            desc: 'We map your data flows, document handling processes, and identify the manual touchpoints slowing your network.'
          }, {
            step: '02',
            title: 'Build',
            desc: 'We train custom agents on your specific carrier networks, compliance rules, and operational systems.'
          }, {
            step: '03',
            title: 'Integration',
            desc: 'We connect agents directly to your TMS, WMS, and communication channels for seamless orchestration.'
          }].map((item, i) => <motion.div key={i} variants={fadeUp} className="relative z-10 flex flex-col items-center text-center group" data-id="element-966">
                <div className="w-16 h-16 rounded-full bg-brand-black border border-white/10 flex items-center justify-center text-xl font-display text-brand-orange mb-8 transition-all duration-500 group-hover:border-brand-orange/50 group-hover:bg-brand-orange/5 group-hover:shadow-[0_0_30px_rgba(219,127,74,0.2)]" data-id="element-967">
                  {item.step}
                </div>
                <h3 className="text-2xl font-medium text-white mb-4 tracking-tight" data-id="element-968">
                  {item.title}
                </h3>
                <p className="text-gray-400 font-light leading-relaxed max-w-sm" data-id="element-969">
                  {item.desc}
                </p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>

      {/* 7. FINAL CTA SECTION */}
      <section className="py-32 relative overflow-hidden" data-id="element-970">
        <div className="absolute inset-0 bg-brand-orange/5" data-id="element-971" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-brand-orange/10 rounded-full blur-[100px] pointer-events-none" data-id="element-972" />

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center" data-id="element-973">
          <motion.h2 initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-5xl md:text-6xl font-display mb-8 leading-[1.0] tracking-tight text-balance" data-id="element-974">
            Stop Managing Data. <br data-id="element-975" />
            <span className="text-brand-orange" data-id="element-976">Start Scaling Logistics.</span>
          </motion.h2>

          <motion.p initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="text-xl text-gray-300 mb-12 font-light max-w-2xl mx-auto" data-id="element-977">
            Let's look at your supply chain operations and identify automation
            opportunities that increase your throughput.
          </motion.p>

          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeUp} className="flex flex-col items-center" data-id="element-978">
            <a href="mailto:sales@aijentik.com" className="inline-block bg-white text-brand-black hover:bg-gray-100 px-10 py-5 rounded-full font-medium text-lg transition-all duration-300 shadow-xl hover:shadow-2xl hover:-translate-y-1 mb-6" data-id="element-979">
              Contact Us — sales@aijentik.com
            </a>
            <p className="text-sm text-gray-500 font-medium" data-id="element-980">
              We'll get back to you within 24 hours.
            </p>
          </motion.div>
        </div>
      </section>
    </main>;
}